import React, { useState, useEffect, useCallback } from "react";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import CustomModal from "./customModal.jsx";
import {
  SelectionModalHeaderActions,
  SelectionModalEmptyCTA,
  SelectionModalRefreshButton,
} from "./SelectionModalQuickLinks";
import axiosInstance from "../../../config/axiosApi/axiosApi.jsx";
import APIs from "../../../Constants/apis.js";

const SupplierSelectionModal = ({
  showModal,
  setShowModal,
  onSubmit,
  compID,
  selectedSuppliers = [],
  modalTitle = "Select Suppliers",
  modalSubTitle = "Select the suppliers for the item",
  isMultiSelect = true,
  showButton = true,
}) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState(selectedSuppliers || []);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Fetch suppliers when modal opens
  const fetchSuppliers = useCallback(async () => {
    if (!compID) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_SUPPLIERS, {
        params: {
          CompID: compID,
          Search: "",
          SortBy: "S_Name",
          SortOrder: "ASC",
          PageNumber: 1,
          PageSize: 1000, // High value to fetch all for selection
        },
      });

      const supplierData = response.data.supplier || [];
      setSuppliers(Array.isArray(supplierData) ? supplierData : []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  }, [compID]);

  useEffect(() => {
    if (showModal) {
      fetchSuppliers();
      setSelectedItems(selectedSuppliers || []);
    }
  }, [showModal, fetchSuppliers, selectedSuppliers]);

  const handleSupplierClick = (supplier) => {
    if (isMultiSelect) {
      setSelectedItems((prev) =>
        prev.some((s) => s.s_ID === supplier.s_ID)
          ? prev.filter((s) => s.s_ID !== supplier.s_ID)
          : [...prev, supplier]
      );
    } else {
      setSelectedItems(supplier);
    }
  };

  const handleSubmit = () => {
    if (isMultiSelect) {
      onSubmit(selectedItems);
    } else if (selectedItems) {
      onSubmit(selectedItems);
    }
    setShowModal(false);
    setSelectedItems(isMultiSelect ? [] : null);
    setSearchTerm("");
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedItems(isMultiSelect ? [] : null);
    setSearchTerm("");
  };

  const filteredSuppliers = suppliers.filter(
    (supplier) =>
      supplier.s_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.s_Description?.toLowerCase().includes(
        searchTerm.toLowerCase()
      ) ||
      supplier.s_Email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isSelected = (supplier) => {
    if (isMultiSelect) {
      return selectedItems.some((s) => s.s_ID === supplier.s_ID);
    }
    return selectedItems?.s_ID === supplier.s_ID;
  };

  const submitDisabled =
    isMultiSelect ? selectedItems.length === 0 : !selectedItems;

  return (
    <CustomModal
      isOpen={showModal}
      onClose={handleClose}
      onSubmit={handleSubmit}
      title={modalTitle}
      subtitle={modalSubTitle}
      modalSize="modal-lg"
      isCentered={true}
      maxHeight="600px"
      showButton={showButton}
      showSubmitButton={true}
      showCancelButton={true}
      submitButtonText="Select"
      submitButtonDisabled={submitDisabled}
      headerActions={<SelectionModalHeaderActions variant="supplier" />}
    >
      <div className="p-3">
        {/* Search Input */}
        <div className="mb-3 d-flex align-items-stretch gap-2">
          <div className="position-relative flex-grow-1">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, description, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                borderRadius: "8px",
                border: `1px solid ${isDarkMode ? "#4a5568" : "#D9D9D9"}`,
                background: isDarkMode ? "#2d3748" : "#F9F9F9",
                color: isDarkMode ? "#e2e8f0" : "#495057",
                padding: "10px 40px 10px 15px",
                transition: "all 0.2s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = themeColor;
                e.target.style.boxShadow = `0 0 0 3px ${themeColor}20`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDarkMode ? "#4a5568" : "#D9D9D9";
                e.target.style.boxShadow = "none";
              }}
            />
            <i
              className="fa fa-search position-absolute"
              style={{
                right: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                color: isDarkMode ? "#a0aec0" : "#666",
              }}
            />
            {searchTerm && (
              <button
                type="button"
                className="btn btn-sm position-absolute"
                style={{
                  right: "35px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: isDarkMode ? "#a0aec0" : "#666",
                  padding: "0",
                  fontSize: "12px",
                }}
                onClick={() => setSearchTerm("")}
                onMouseEnter={(e) => {
                  e.target.style.background = isDarkMode
                    ? "#4a5568"
                    : "#f8f9fa";
                  e.target.style.color = isDarkMode ? "#e2e8f0" : "#495057";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "none";
                  e.target.style.color = isDarkMode ? "#a0aec0" : "#666";
                }}
              >
                <i className="fa fa-times"></i>
              </button>
            )}
          </div>
          <SelectionModalRefreshButton
            onClick={() => fetchSuppliers()}
            loading={loading}
          />
        </div>

        {/* Suppliers List */}
        <div
          className="border rounded"
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            border: `1px solid ${isDarkMode ? "#4a5568" : "#D9D9D9"}`,
            backgroundColor: isDarkMode ? "#2d3748" : "#ffffff",
          }}
        >
          {loading ? (
            <div className="text-center p-4">
              <div
                className="spinner-border"
                role="status"
                style={{ color: themeColor }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <p
                className="mt-2"
                style={{ color: isDarkMode ? "#a0aec0" : "#6c757d" }}
              >
                Loading suppliers...
              </p>
            </div>
          ) : !loading && (!suppliers || suppliers.length === 0) ? (
            <SelectionModalEmptyCTA variant="supplier" />
          ) : filteredSuppliers.length > 0 ? (
            <div
              className="p-2"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              {filteredSuppliers.map((supplier, index) => {
                const selected = isSelected(supplier);
                return (
                  <div
                    key={supplier.s_ID || index}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSupplierClick(supplier);
                      }
                    }}
                    onClick={() => handleSupplierClick(supplier)}
                    style={{
                      background: selected
                        ? isDarkMode
                          ? `${themeColor}22`
                          : `${themeColor}14`
                        : isDarkMode
                        ? "#404040"
                        : "#ffffff",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      border: selected
                        ? `2px solid ${themeColor}`
                        : `1px solid ${isDarkMode ? "#555" : "#e9ecef"}`,
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      boxShadow: selected
                        ? `0 1px 6px ${themeColor}20`
                        : isDarkMode
                        ? "0 1px 3px rgba(0,0,0,0.22)"
                        : "0 1px 2px rgba(0,0,0,0.05)",
                    }}
                    onMouseEnter={(e) => {
                      if (!selected) {
                        const el = e.currentTarget;
                        el.style.borderColor = themeColor;
                        el.style.boxShadow = `0 2px 8px ${themeColor}18`;
                        el.style.transform = "translateY(-1px)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selected) {
                        const el = e.currentTarget;
                        el.style.borderColor = isDarkMode ? "#555" : "#e9ecef";
                        el.style.boxShadow = isDarkMode
                          ? "0 1px 3px rgba(0,0,0,0.22)"
                          : "0 1px 2px rgba(0,0,0,0.05)";
                        el.style.transform = "translateY(0)";
                      }
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center gap-2">
                      <div
                        className="d-flex align-items-center flex-grow-1"
                        style={{ minWidth: 0 }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: 600,
                              lineHeight: 1.25,
                              marginBottom: "2px",
                              color: selected
                                ? themeColor
                                : isDarkMode
                                ? "#f8fafc"
                                : "#212529",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={supplier.s_Name}
                          >
                            {supplier.s_Name}
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              lineHeight: 1.3,
                              color: isDarkMode ? "#94a3b8" : "#64748b",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={[
                              supplier.s_Email || "No email on file",
                              supplier.s_Type || "General",
                              supplier.s_Description,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          >
                            {supplier.s_Email || "No email on file"}
                            {" · "}
                            {supplier.s_Type || "General"}
                            {supplier.s_Description
                              ? ` · ${supplier.s_Description}`
                              : ""}
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          width: "22px",
                          height: "22px",
                          borderRadius: "5px",
                          border: selected
                            ? `2px solid ${themeColor}`
                            : `2px solid ${
                                isDarkMode ? "#666" : "#e9ecef"
                              }`,
                          background: selected ? themeColor : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          transition: "all 0.2s ease",
                          cursor: "pointer",
                        }}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          handleSupplierClick(supplier);
                        }}
                      >
                        {selected && (
                          <i
                            className="fa fa-check"
                            style={{
                              fontSize: "10px",
                              color: "#fff",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center p-4">
              <div
                className="border-2 border-dashed rounded p-4"
                style={{
                  borderColor: isDarkMode ? "#4a5568" : "#D9D9D9",
                  borderStyle: "dashed",
                  backgroundColor: isDarkMode ? "#1a202c" : "#f8f9fa",
                }}
              >
                <i
                  className="fa fa-search mb-2"
                  style={{
                    fontSize: "24px",
                    color: isDarkMode ? "#a0aec0" : "#6c757d",
                  }}
                ></i>
                <p
                  className="mb-0"
                  style={{
                    fontSize: "13px",
                    color: isDarkMode ? "#a0aec0" : "#6c757d",
                  }}
                >
                  {searchTerm
                    ? "No suppliers found matching your search"
                    : "No suppliers available"}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default SupplierSelectionModal;